import {
  type PermissionContext,
  permissionsOperations,
} from '@ardoq/access-control';
import { scenarioAccessControlInterface } from 'resourcePermissions/accessControlHelpers/scenarioAccessControlInterface';
import {
  APIConstraintEnforcementMode,
  type APIComponentAttributes,
  type APIReferenceAttributes,
  type APIReferenceType,
  type ArdoqId,
} from '@ardoq/api-types';
import { activeScenarioOperations } from '@ardoq/core';
import type { ContextShape, ScenarioModeState } from '@ardoq/data-model';
import { referenceAccessControlOperation } from 'resourcePermissions/accessControlHelpers/reference';
import { getFeatureContext } from 'streams/featureContext$';
import { lockOperations } from 'lockedComponents/lockOperations';
import {
  Permissions,
  isDisabled,
} from 'streams/currentUserPermissions/permissionInterface';
import { type IntendedRefTypes } from 'utils/metamodelConstraints';
import {
  type ReferenceModel,
  type ReferenceOptionsState,
  type ToggleLockCapability,
} from './referenceOptions$';
import { calculateConstraintViolation } from './referenceOptionsConstraints';
import { type I18nLocale } from '@ardoq/i18n';

const getCommonProps = (hasWorkspaceWriteAccess: boolean) => ({
  hasWorkspaceWriteAccess,
  isViewVersionHistoryDisabled: isDisabled(Permissions.REFERENCE_VIEW_HISTORY),
});

const computeCanEditScenario = (
  activeScenarioState: ScenarioModeState,
  permissionContext: PermissionContext
): boolean =>
  !activeScenarioOperations.isInScenarioMode(activeScenarioState) ||
  scenarioAccessControlInterface.canEditActiveScenario(
    permissionContext,
    activeScenarioState
  );

const buildReferencesForComponent = (
  componentId: ArdoqId,
  allRefs: APIReferenceAttributes[],
  displayNameById: ReadonlyMap<ArdoqId, string | null>
): ReferenceModel[] => {
  const sourceRefs = allRefs
    .filter(ref => ref.source === componentId)
    .map(ref => ({
      _id: ref._id,
      arrowDirection: 'right' as const,
      leftName: displayNameById.get(ref.source) ?? 'NAME MISSING',
      rightName: displayNameById.get(ref.target) ?? 'NAME MISSING',
    }));

  const targetRefs = allRefs
    .filter(ref => ref.target === componentId)
    .map(ref => ({
      _id: ref._id,
      arrowDirection: 'left' as const,
      leftName: displayNameById.get(ref.target) ?? 'NAME MISSING',
      rightName: displayNameById.get(ref.source) ?? 'NAME MISSING',
    }));

  return [...sourceRefs, ...targetRefs];
};

const computeToggleLock = (
  reference: APIReferenceAttributes,
  sourceComponent: APIComponentAttributes | undefined,
  permissionContext: PermissionContext
): ToggleLockCapability => {
  const featureContext = getFeatureContext();
  const canToggleLock =
    lockOperations.isLockingEnabled(featureContext) &&
    (reference.lock
      ? referenceAccessControlOperation.canUnlock({
          permissionContext,
          reference,
        })
      : referenceAccessControlOperation.canEditReference({
          reference,
          sourceComponent,
          permissionContext,
          featureContext,
        }));
  if (
    !canToggleLock ||
    !permissionsOperations.isLoadedPermissionContext(permissionContext)
  ) {
    return { canToggleLock: false };
  }
  return { canToggleLock: true, currentUserId: permissionContext.user._id };
};

const buildForReferenceContext = (args: {
  reference: APIReferenceAttributes;
  referenceId: ArdoqId;
  permissionContext: PermissionContext;
  canEditScenario: boolean;
  sourceComponent: APIComponentAttributes | undefined;
  targetComponent: APIComponentAttributes | undefined;
}): Partial<ReferenceOptionsState> => {
  const {
    reference,
    referenceId,
    permissionContext,
    canEditScenario,
    sourceComponent,
    targetComponent,
  } = args;
  return {
    canEditReference:
      canEditScenario &&
      referenceAccessControlOperation.canEditReference({
        reference,
        sourceComponent,
        permissionContext,
        featureContext: getFeatureContext(),
      }),
    canEditReferencesTargetComponent:
      canEditScenario &&
      !!targetComponent &&
      referenceAccessControlOperation.canEditComponentReferences({
        component: targetComponent,
        permissionContext,
        featureContext: getFeatureContext(),
      }),
    references: [],
    contextReferenceId: referenceId,
    locked: lockOperations.isLocked(reference.lock, getFeatureContext()),
    toggleLock: computeToggleLock(
      reference,
      sourceComponent,
      permissionContext
    ),
  };
};

const buildForComponentContext = (args: {
  componentId: ArdoqId;
  canEditScenario: boolean;
  allRefs: APIReferenceAttributes[];
  displayNameById: ReadonlyMap<ArdoqId, string | null>;
  component: APIComponentAttributes | undefined;
  permissionContext: PermissionContext;
}): Partial<ReferenceOptionsState> => {
  const {
    componentId,
    canEditScenario,
    allRefs,
    displayNameById,
    component,
    permissionContext,
  } = args;

  return {
    canEditReference:
      canEditScenario &&
      !!component &&
      referenceAccessControlOperation.canEditComponentReferences({
        component,
        permissionContext,
        featureContext: getFeatureContext(),
      }),
    references: buildReferencesForComponent(
      componentId,
      allRefs,
      displayNameById
    ),
    contextReferenceId: undefined,
  };
};

type MapContextToReferenceOptionsArgs = {
  contextState: ContextShape;
  permissionContext: PermissionContext;
  activeScenarioState: ScenarioModeState;
  intendedRefTypes: IntendedRefTypes | null;
  constraintEnforcementMode: APIConstraintEnforcementMode;
  i18nLocale: I18nLocale;
  hasWorkspaceWriteAccess: boolean;
  allRefs: APIReferenceAttributes[];
  reference: APIReferenceAttributes | undefined;
  referenceTypes: readonly APIReferenceType[];
  sourceTypeName: string | null;
  targetTypeName: string | null;
  displayNameById: ReadonlyMap<ArdoqId, string | null>;
  componentsById: Readonly<Record<ArdoqId, APIComponentAttributes>>;
};

export const mapContextToReferenceOptions = ({
  contextState,
  permissionContext,
  activeScenarioState,
  intendedRefTypes,
  constraintEnforcementMode,
  i18nLocale,
  hasWorkspaceWriteAccess,
  allRefs,
  reference,
  referenceTypes,
  sourceTypeName,
  targetTypeName,
  displayNameById,
  componentsById,
}: MapContextToReferenceOptionsArgs): ReferenceOptionsState => {
  const commonProps = getCommonProps(hasWorkspaceWriteAccess);
  const referenceId = contextState.referenceId;
  const canEditScenario = computeCanEditScenario(
    activeScenarioState,
    permissionContext
  );
  const reverseConstraintViolation = calculateConstraintViolation({
    reference,
    referenceTypes,
    sourceTypeName,
    targetTypeName,
    intendedRefTypes,
    constraintEnforcementMode,
  });

  const defaults = {
    canEditReference: false,
    canEditReferencesTargetComponent: false,
    locked: false,
    toggleLock: { canToggleLock: false } as ToggleLockCapability,
    contextReferenceId: undefined as ArdoqId | undefined,
    references: [] as ReferenceModel[],
  };

  if (reference) {
    const referenceContextProps = buildForReferenceContext({
      reference,
      referenceId: referenceId!,
      permissionContext,
      canEditScenario,
      sourceComponent: componentsById[reference.source],
      targetComponent: componentsById[reference.target],
    });
    return {
      ...defaults,
      ...commonProps,
      ...referenceContextProps,
      reverseConstraintViolation,
      i18nLocale,
    };
  }

  const componentId = contextState.componentId;
  if (!componentId) {
    return {
      ...defaults,
      ...commonProps,
      references: [],
      reverseConstraintViolation,
      i18nLocale,
    };
  }

  const componentContextProps = buildForComponentContext({
    componentId,
    canEditScenario,
    allRefs,
    displayNameById,
    component: componentsById[componentId],
    permissionContext,
  });
  return {
    ...defaults,
    ...commonProps,
    ...componentContextProps,
    reverseConstraintViolation,
    i18nLocale,
  };
};
